import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, computed, ErrorHandler, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';

import { map } from 'rxjs';

import { emailValidator, ValidationErrorDirective } from '@rent2end/shared/forms';
import { LogoComponent } from '@rent2end/ui';
import { TranslationPipe, TranslationStore } from '@rent2end/core/translation';
import { windowSizeClasses } from '@rent2end/utils/ui';

import { AuthStore } from '../state/auth.store';
import { SignInProvidersComponent } from './providers/sign-in-providers.component';

@Component({
  selector: 'r2e-sign-up',
  imports: [
    MatButton,
    MatCheckbox,
    MatIcon,
    MatIconButton,
    ReactiveFormsModule,
    RouterLink,
    LogoComponent,
    SignInProvidersComponent,
    TranslationPipe,
    ValidationErrorDirective
  ],
  templateUrl: './sign-up.component.html',
  styleUrl: './sign.component.scss',
})
export class SignUpComponent {
  private authStore = inject(AuthStore);
  private breakpointObserver = inject(BreakpointObserver);
  private errorHandler = inject(ErrorHandler);
  private translationStore = inject(TranslationStore);

  signUpForm = new FormGroup({
    email: new FormControl<string>('', { nonNullable: true, validators: [emailValidator, Validators.required] }),
    fullName: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    marketingConsent: new FormControl<boolean>(false, { nonNullable: true }),
    password: new FormControl<string>('', { nonNullable: true, validators: [Validators.minLength(6), Validators.required] }),
    termsAccepted: new FormControl<boolean>(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
  });

  dataProcessingUrl = computed(() => `https://rent2end.pl/${this.translationStore.currentLanguage()}/umowa-powierzenia-danych/`);
  logoVariant = toSignal(
    this.breakpointObserver
      .observe([windowSizeClasses.compact, windowSizeClasses.medium])
      .pipe(
        map(state => state.matches ? 'vertical-logo' : 'horizontal-logo'),
      ),
    { requireSync: true }
  );
  marketingConsent = toSignal(this.signUpForm.controls.marketingConsent.valueChanges, { initialValue: false });
  passwordHidden = signal(true);
  privacyPolicyUrl = computed(() => `https://rent2end.pl/${this.translationStore.currentLanguage()}/polityka-prywatnosci/`);
  termsUrl = computed(() => `https://rent2end.pl/${this.translationStore.currentLanguage()}/regulamin/`);

  signUp() {
    if (this.signUpForm.valid) {
      const { email, fullName, marketingConsent, password } = this.signUpForm.getRawValue();
      this.authStore
        .createUser(email, password, fullName, marketingConsent)
        .then(() => this.signUpForm.reset())
        .catch((error) => this.errorHandler.handleError(error));
    }
  }

  togglePasswordVisibility() {
    this.passwordHidden.update(hidden => !hidden);
  }
}
